import * as fabric from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import ListItemCreateButton from '../../../../../components/custom/ListItemCreateButton';
import { useConfirmation } from '../../../../../components/dialogs';
import { IWindowComponentProps, useWindow } from '../../../../../components/windows/WindowContext';
import LocationConfigTerminalServiceCreateWindow from './LocationConfigTerminalServiceCreateWindow';
import LocationConfigTerminalServiceEditWindow from './LocationConfigTerminalServiceEditWindow';

export interface ITerminalService {
    id: number;
    serviceId: number;
    serviceName: string | null;
    terminalId: number;
    groupId: number | null;
    groupName: string | null;
    orderLevel: number;
    isTerminalHelpService: boolean;
    floor: number;
}

interface LocationConfigTerminalWindowProps {
    terminalId: number;
    organizationInformationId: number;
}

const LocationConfigTerminalWindow: FC<IWindowComponentProps<LocationConfigTerminalWindowProps>> = (props) => {
    const {
        componentProps: { terminalId, organizationInformationId },
        onClose
    } = props;

    const [terminalServices, setTerminalServices] = useState<ITerminalService[] | null>(null);

    const [isHelpServiceButtontsDisabled, setIsHelpServiceButoonDisabled] = useState<boolean>(false);

    const { confirmAsync } = useConfirmation();
    const { openWindow } = useWindow();

    const handleApprove = async (id: number) => {
        try {
            const { success } = await confirmAsync('Paslaugos atskyrimas nuo terminalo', 'Ar tikrai norite atskirti paslaugą nuo terminalo?');

            if (!success) {
                return;
            }
            await api.terminalServices.deleteTerminalService(id);
            toast.success('Paslauga sėkmingai atskirta nuo terminalo', '');
            fetchData();
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const handleTerminalHelpServiceChange = async (terminalId: number, serviceId: number | null) => {
        try {
            setIsHelpServiceButoonDisabled(true);
            await api.administration.updateTerminalHelpService({
                terminalId,
                serviceId
            });
            await fetchData();
            serviceId === null
                ? toast.warning('Terminalas neturi pagalbinės paslaugos')
                : toast.success('Terminalo pagalbinė paslauga sėkmingai priskirta');
        } catch (ex) {
            toast.error(ex.error[0]);
        } finally {
            setIsHelpServiceButoonDisabled(false);
        }
    };
    const _allItemsColumns = [
        {
            key: 'column1',
            name: 'Pavadinimas',
            fieldName: 'serviceName',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true
        },
        {
            key: 'column2',
            name: 'Grupė',
            fieldName: 'groupName',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true
        },
        {
            key: 'column3',
            name: 'Paslaugos eil. nr. sąraše',
            fieldName: 'orderLevel',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true
        },
        {
            key: 'column4',
            name: 'Grupės eil. nr. sąraše',
            fieldName: 'groupServiceOrderLevel',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true
        },
        {
            key: 'column5',
            name: 'Pagalbinė paslauga',
            minWidth: 200,
            maxWidth: 250,
            isResizable: true,
            onRender: (item: ITerminalService) => (
                <div>
                    {item.isTerminalHelpService ? (
                        <fabric.IconButton
                            onClick={() => {
                                handleTerminalHelpServiceChange(terminalId, null);
                            }}
                            disabled={isHelpServiceButtontsDisabled}
                            iconProps={{
                                iconName: 'ChromeClose',
                                styles: {
                                    root: {
                                        color: fabric.getTheme().palette.red
                                    }
                                }
                            }}
                        />
                    ) : (
                        <fabric.IconButton
                            disabled={isHelpServiceButtontsDisabled}
                            onClick={() => handleTerminalHelpServiceChange(terminalId, item.serviceId)}
                            iconProps={{
                                iconName: 'CheckMark',
                                styles: {
                                    root: {
                                        fontSize: '25px'
                                    }
                                }
                            }}
                        />
                    )}
                </div>
            )
        },
        {
            key: 'column6',
            name: 'Redagavimas',
            minWidth: 100,
            maxWidth: 100,
            isResizable: true,
            onRender: (item: ITerminalService) => {
                return (
                    <fabric.IconButton
                        onClick={() => {
                            openLocationConfigTerminalServiceEditWindow(item.id);
                        }}
                        iconProps={{
                            iconName: 'PageEdit'
                        }}
                    />
                );
            }
        },
        {
            key: 'column7',
            name: 'Ištrynimas',
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: ITerminalService) => {
                return (
                    <fabric.IconButton
                        onClick={async () => await handleApprove(item.id)}
                        iconProps={{
                            iconName: 'Trash',
                            style: { color: fabric.getTheme().palette.red }
                        }}
                    />
                );
            }
        }
    ];
    const fetchData = async () => {
        try {
            const terminalServicesResp = await api.terminalServices.getTerminalServices(terminalId);
            setTerminalServices(terminalServicesResp);
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const openLocationConfigTerminalServiceCreateWindow = async () => {
        try {
            LocationConfigTerminalServiceCreateWindow.defaultProps = {
                componentProps: {
                    fetchData: fetchData,
                    floor: terminalServices.length > 0 ? terminalServices[0].floor : 0,
                    terminalId: terminalId,
                    organizationInformationId: parseInt(organizationInformationId.toString())
                }
            };
            openWindow({
                title: 'Paslaugos priskyrimas terminalui',
                component: LocationConfigTerminalServiceCreateWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const openLocationConfigTerminalServiceEditWindow = async (editableTerminalServiceId: number) => {
        try {
            LocationConfigTerminalServiceEditWindow.defaultProps = {
                componentProps: {
                    fetchData: fetchData,
                    floor: terminalServices.length > 0 ? terminalServices[0].floor : 0,
                    editableTerminalServiceId: editableTerminalServiceId,
                    terminalId: terminalId,
                    organizationInformationId: parseInt(organizationInformationId.toString())
                }
            };
            openWindow({
                title: 'Terminalui priskirtos paslaugos redagavimas',
                component: LocationConfigTerminalServiceEditWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    return (
        <>
            <ToastContainer theme="colored" />
            <div style={{ textAlign: 'center' }}>
                <fabric.Text style={{ fontWeight: 'lighter' }} variant="xxLarge">
                    Terminale atvaizduojamų paslaugų pasirinkimas
                </fabric.Text>
            </div>
            <div className="ms-Grid-row" dir="rtl" style={{ textAlign: 'right', marginTop: '50px' }}>
                <ListItemCreateButton openCreateModal={openLocationConfigTerminalServiceCreateWindow} text="Pridėti" />
            </div>
            {!terminalServices && <KendoLoading />}
            {terminalServices && (
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-md12">
                        <fabric.DetailsList
                            items={terminalServices}
                            columns={_allItemsColumns}
                            checkboxVisibility={fabric.CheckboxVisibility.hidden}
                            selectionMode={fabric.SelectionMode.none}
                            disableSelectionZone={true}
                            setKey={'2'}
                            enterModalSelectionOnTouch={true}
                            selectionPreservedOnEmptyClick={true}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default LocationConfigTerminalWindow;
